<template>
  <div>
    <AwesomeLoader
      :visible.sync="loading"
      :fullscreen="false"
      :darkMode="false"
      message="Loading workspace..."
      lottieUrl="https://assets6.lottiefiles.com/packages/lf20_x62chJ.json"
    />
    <el-card class="register-card" v-if="pageType === 1&& !loading">
      <div class="logo-container">
        <img :src="getLogoInfo" alt="Kodefast Logo" class="logo" />
        <p class="sub-title">
          Create your account to get started as Tenant
        </p>
      </div>
      <el-form :model="form" :rules="rules" ref="formRef" label-position="top">
        <div class="row">
          <el-form-item label="First Name" prop="firstName" class="half">
            <el-input v-model="form.firstName" placeholder="John" />
          </el-form-item>
          <el-form-item label="Last Name" prop="lastName" class="half">
            <el-input v-model="form.lastName" placeholder="Doe" />
          </el-form-item>
        </div>
        <el-form-item label="Email" prop="email">
          <el-input v-model="form.email" placeholder="you@company.com" />
        </el-form-item>
        <el-form-item label="Phone Number" prop="phone">
          <div ref="phoneWrapper">
            <input
              ref="phone"
              type="tel"
              class="form-control"
              autocomplete="new-phone"
              pattern="\d*"
              inputmode="numeric"
              @keypress="onlyAllowNumbers"
            />
          </div>
        </el-form-item>
        <el-form-item label="Tenant Code" prop="tenantCode">
          <div class="code-group">
            <el-input v-model="form.tenantCode" />
          </div>
        </el-form-item>
        <el-button type="primary" class="submit-button" :loading="loading" @click="submitForm">
          Register Tenant
        </el-button>
        <p class="login-link">
          Already registered? <router-link to="/signin/tenant" class="fw-bold" :style="getPrimaryColor">
              LogIn
            </router-link> 
        </p>
      </el-form>
    </el-card>
    <accessdenied v-else-if="pageType === 2&& !loading" :errorMessage="errorMessage" />
  </div>
</template>

<script>
import app_config from "@/config/app";
import axios from "@/config/axios";
export default {
  name: "NoCodeBackground",
  data() {
    return {
      pageType: 0,
      errorMessage: '',
      value: '',
      application_config: app_config,
      config: {
        overlayColor: 'rgba(0, 0, 0, 0.2)'
      },
      defaultLogo: "../../assets/logo.webp",
      company_info: {},
      registration_config: {},
      form: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        tenantCode: "",
        phone_code: null
      },
      iti: null,
      showPassword: false,
      loading: false,
      rules: {
        firstName: [{ required: true, message: "First name is required", trigger: "blur" }],
        lastName: [{ required: true, message: "Last name is required", trigger: "blur" }],
        email: [
          { required: true, message: "Email is required", trigger: "blur" },
          { type: "email", message: "Invalid email format", trigger: "blur" }
        ],
        tenantCode: [{ required: true, message: "Tenant code is required", trigger: "blur" }]
      }
    };
  },
  components: {
    accessdenied: () => import("./accessdenied.vue"),
    AwesomeLoader: () => import("./loader.vue")
  },
  computed: {
    getLogoInfo() {
      return this.company_info && this.company_info.logo ? `${this.application_config.S3_BUCKET_URL}/${this.company_info.logo}` : this.defaultLogo;
    }
  },
  created() {
    this.form.tenantCode = this.$route.params.unique_code;
  },
  async mounted() {
    document.documentElement.style.setProperty('--overlay-color', this.config.overlayColor);
    await this.validateCode();
    this.$nextTick(() => {
      this.initPhoneInput();
    });
  },
  methods: {
    async initPhoneInput() {
      let defaultCountry = await this.getCountryFromAPI();
      const input = this.$refs.phone;
      if (!input || !window.intlTelInput) {
        console.error("intlTelInput or input not found");
        return;
      }
      this.iti = window.intlTelInput(input, {
        initialCountry: defaultCountry || 'auto',
        strictMode: true,
        separateDialCode: true,
        nationalMode: false,
        preferredCountries: ['in', 'us', 'gb'],
        geoIpLookup: async (callback) => {
          if (defaultCountry) {
            callback(defaultCountry);
            return;
          }
          try {
            const res = await fetch('https://ipinfo.io/json');
            const data = await res.json();
            callback(data?.country?.toLowerCase() || 'us');
          } catch (e) {
            console.warn('GeoIP lookup failed:', e);
            callback('us');
          }
        },
        utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@17/build/js/utils.js',
        dropdownContainer: document.body,
        allowDropdown: true
      });
    },
    async getCountryFromAPI() {
      try {
        return this.registration_config ? 'us' : null;
      } catch (error) {
        console.warn('Country API failed:', error);
        return null;
      }
    },
    onlyAllowNumbers(event) {
      const key = event.key;
      const isAllowed = /[0-9]/.test(key) || ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(key);
      if (!isAllowed) {
        event.preventDefault();
      }
    },
    async validateCode() {
      this.loading = true;
      if (this.form.tenantCode) {
        try {
          let info = await axios.get(`/ap/verify/${this.form.tenantCode}`);
          this.registration_config = info.data.data;
          this.company_info = info.data.data.company_id;
          this.pageType = 1;
          this.loading = false;
          this.errorMessage = "";
        } catch (err) {
          this.loading = false;
          this.pageType = 2;
          this.errorMessage = err?.response?.data?.message || err?.message;
        }
      }
      else{
         this.pageType = 1;
          this.loading = false;
      }
    },
    async submitForm() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid || !this.iti) return;

        const fieldConfig = this.iti;
        if (!fieldConfig.isValidNumber()) {
          this.$message.error("Invalid phone number");
          return;
        }

        const countryData = fieldConfig.getSelectedCountryData();
        const nationalNumber = fieldConfig.getNumber(window.intlTelInputUtils.numberFormat.NATIONAL);
        const internationalNumber = fieldConfig.getNumber(window.intlTelInputUtils.numberFormat.INTERNATIONAL);
        const e164Number = fieldConfig.getNumber(window.intlTelInputUtils.numberFormat.E164);
        const uriNumber = fieldConfig.getNumber(window.intlTelInputUtils.numberFormat.RFC3966);

        this.form.phone = e164Number;
        this.form.phone_code = {
          country: countryData.name,
          iso2: countryData.iso2,
          dialCode: countryData.dialCode,
          nationalNumber,
          internationalNumber,
          e164Number,
          uriNumber
        };

        this.loading = true;
        try {
          let info = await axios.post(`/ap/reg/${this.form.tenantCode}`, this.form);
          setTimeout(() => {
            this.loading = false;
            this.$message.success("Tenant registered successfully!");
            this.$router.push({
              path: `/${this.form.tenantCode}/otp?id=${info.data.contact._id}&mail=${info.data.contact.email}`
            });
            this.resetForm();
          }, 1000);
        } catch (err) {
          this.loading = false;
          this.$message.error(err.response.data.message || err.message);
        }
      });
    },
    resetForm() {
      this.form = {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        tenantCode: "",
        phone_code: null
      };
    }
  },
  beforeDestroy() {
    if (this.iti) {
      this.iti.destroy();
    }
  }
};
</script>

<style scoped>


.el-card__body,
  .el-main {
    padding: 0px !important;
  }
  .el-form-item {
    margin-bottom: 5px;
  }
  .register-card {
    width: 100%;
    /* max-width: 440px; */
    /* padding: 20px 20px 25px; */
    border-radius: 12px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
  }
  .logo-container {
    text-align: center;
    margin-bottom: 18px;
  }
  
  .sub-title {
    font-size: 13px;
    color: #777;
    margin-top: 2px;
  }
  .row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
  }
  
  .half {
    flex: 1;
  }
  
  .code-group {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  
  .submit-button {
    width: 100%;
    margin-top: 10px;
  }

  .logo {
    width: 10em;
    @media (max-width: 1024px) {
      width: 10em;
    }
    @media (max-width: 480px) {
      width: 8em;
    }
  }



  #phone {
    width: 100%;
    padding: 10px;
    border-radius: 4px;
    border: 1px solid #dcdfe6;
  }
    
  .login-link {
    text-align: center;
    margin-top: 10px;
    font-size: 13px;
    color: #666;
  }
  
  .login-link a {
    color: #409eff;
    text-decoration: none;
  }
</style>
